import { MortgageTerms } from './api/httpHandlers'

export type AppStatus =
  | 'initializing'
  | 'ready'
  | 'fetching'
  | 'formError'
  | 'userIsTyping'
  | 'success'

export interface UserValues {
  estimatedValue: number
  mortgage: number
  wantedLoanAmount: number
}

export interface InterestRateAppState {
  status: AppStatus
  mortgageTerms: MortgageTerms | null
  threeMonthInterestRate: number
  showResult: boolean
  formIsValid: boolean
  userValues: UserValues
  priceMatrixDifference: number
  kilowattLoanDifference: number
  effectiveInterest: number
  hasFetchedSucessfully: boolean
}

export const initialState: InterestRateAppState = {
  status: 'initializing',
  mortgageTerms: null,
  threeMonthInterestRate: 0,
  showResult: false,
  formIsValid: false,
  userValues: {
    estimatedValue: 0,
    mortgage: 0,
    wantedLoanAmount: 0,
  },
  priceMatrixDifference: 0,
  kilowattLoanDifference: 0,
  effectiveInterest: 0,
  hasFetchedSucessfully: false,
}

export type AppAction =
  | {
      type: 'statusChange'
      status: AppStatus
    }
  | {
      type: 'fieldsAreValid'
      fields: {
        estimatedValue: number
        mortgage: number
        wantedLoanAmount: number
      }
    }
  | {
      type: 'appDataFetched'
      mortgageTerms: MortgageTerms
      threeMonthInterestRate: number
    }
  | {
      type: 'resultSectionDataFetched'
      priceMatrixDifference: number
      kilowattLoanDifference: number
      effectiveInterest: number
    }
  | {
      type: 'setHasFetchedSucessfully'
    }
  | {
      type: 'error'
    }

export function reducer(state: InterestRateAppState, action: AppAction): InterestRateAppState {
  const mode = import.meta.env.MODE
  switch (action.type) {
    case 'statusChange':
      if (action.status === 'userIsTyping') {
        return {
          ...state,
          status: action.status,
          formIsValid: false,
        }
      } else {
        return {
          ...state,
          status: action.status,
        }
      }
    case 'fieldsAreValid':
      return {
        ...state,
        userValues: {
          ...state.userValues,
          estimatedValue: action.fields.estimatedValue,
          mortgage: action.fields.mortgage,
          wantedLoanAmount: action.fields.wantedLoanAmount,
        },
        formIsValid: true,
      }
    case 'appDataFetched': {
      const { mortgageTerms, threeMonthInterestRate } = action
      return {
        ...state,
        mortgageTerms,
        threeMonthInterestRate,
        status: 'ready',
      }
    }
    case 'resultSectionDataFetched': {
      const { priceMatrixDifference, kilowattLoanDifference, effectiveInterest } = action
      return {
        ...state,
        priceMatrixDifference,
        kilowattLoanDifference,
        effectiveInterest,
        status: 'success',
      }
    }
    case 'setHasFetchedSucessfully': {
      return {
        ...state,
        hasFetchedSucessfully: true,
      }
    }
    case 'error': {
      throw new Error('Error')
    }
    default: {
      if (mode !== 'production') console.error('Unknown action: ' + JSON.stringify(action, null, 2))
      throw new Error('Unknown action')
    }
  }
}
